const ACTION_BUTTON_VARIANTS = {
  fold: 'action-button--fold',
  check: 'action-button--check',
  call: 'action-button--call',
  raise: 'action-button--raise',
  allIn: 'action-button--all-in'
};

const ACTION_SOUND_KEYS = {
  fold: 'fold',
  check: 'check',
  call: 'commit',
  raise: 'commit',
  allIn: 'all-in'
};

const EVENT_SOUND_KEYS = {
  showCards: 'show-cards',
  handSettled: 'hand-settled',
  handStarted: 'hand-started'
};

const SOUND_PATTERNS = {
  fold: [
    { frequency: 240, durationMs: 70, gain: 0.045, offsetMs: 0, type: 'sawtooth' },
    { frequency: 180, durationMs: 90, gain: 0.03, offsetMs: 55, type: 'triangle' }
  ],
  check: [
    { frequency: 720, durationMs: 40, gain: 0.026, offsetMs: 0, type: 'square' }
  ],
  commit: [
    { frequency: 420, durationMs: 50, gain: 0.05, offsetMs: 0, type: 'triangle' },
    { frequency: 520, durationMs: 45, gain: 0.038, offsetMs: 68, type: 'triangle' }
  ],
  'all-in': [
    { frequency: 330, durationMs: 90, gain: 0.05, offsetMs: 0, type: 'sawtooth' },
    { frequency: 392, durationMs: 90, gain: 0.05, offsetMs: 110, type: 'sawtooth' },
    { frequency: 494, durationMs: 120, gain: 0.055, offsetMs: 220, type: 'sawtooth' }
  ],
  'show-cards': [
    { frequency: 660, durationMs: 55, gain: 0.032, offsetMs: 0, type: 'triangle' },
    { frequency: 880, durationMs: 70, gain: 0.028, offsetMs: 65, type: 'triangle' }
  ],
  'hand-settled': [
    { frequency: 520, durationMs: 60, gain: 0.034, offsetMs: 0, type: 'sine' },
    { frequency: 390, durationMs: 90, gain: 0.03, offsetMs: 70, type: 'triangle' }
  ],
  'hand-started': [
    { frequency: 440, durationMs: 45, gain: 0.03, offsetMs: 0, type: 'square' },
    { frequency: 554, durationMs: 55, gain: 0.026, offsetMs: 55, type: 'square' }
  ]
};

let sharedAudioContext = null;

function browserWindowFrom(options = {}) {
  if (options.browserWindow) {
    return options.browserWindow;
  }
  if (typeof window === 'undefined') {
    return null;
  }
  return window;
}

function getSharedAudioContext(browserWindow) {
  if (!browserWindow) {
    return null;
  }
  const AudioContextClass = browserWindow.AudioContext || browserWindow.webkitAudioContext;
  if (!AudioContextClass) {
    return null;
  }
  if (!sharedAudioContext) {
    sharedAudioContext = new AudioContextClass();
  }
  return sharedAudioContext;
}

function scheduleTone(audioContext, tone, startTime) {
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  oscillator.type = tone.type || 'triangle';
  oscillator.frequency.setValueAtTime(tone.frequency, startTime);
  gainNode.gain.setValueAtTime(0.0001, startTime);
  gainNode.gain.exponentialRampToValueAtTime(tone.gain, startTime + 0.01);
  gainNode.gain.exponentialRampToValueAtTime(0.0001, startTime + (tone.durationMs / 1000));
  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);
  oscillator.start(startTime);
  oscillator.stop(startTime + (tone.durationMs / 1000) + 0.03);
}

function speakAllIn(browserWindow) {
  if (!browserWindow?.speechSynthesis || typeof browserWindow.SpeechSynthesisUtterance !== 'function') {
    return false;
  }
  const utterance = new browserWindow.SpeechSynthesisUtterance('All in');
  utterance.lang = 'en-US';
  utterance.rate = 1.02;
  browserWindow.speechSynthesis.cancel();
  browserWindow.speechSynthesis.speak(utterance);
  return true;
}

export function actionButtonClassName(actionType) {
  return ['action-button', ACTION_BUTTON_VARIANTS[actionType] || 'action-button--check'].join(' ');
}

export function soundKeyForAction(actionType) {
  return ACTION_SOUND_KEYS[actionType] || '';
}

export function soundKeyForEvent(event) {
  return EVENT_SOUND_KEYS[event?.type] || soundKeyForAction(event?.type);
}

export function soundSignatureForEvent(event) {
  const soundKey = soundKeyForEvent(event);
  if (!soundKey) {
    return '';
  }
  return [
    soundKey,
    event?.userId || '',
    event?.createdAt || '',
    event?.handNumber || '',
    event?.amount || ''
  ].join(':');
}

function playSoundKey(soundKey, options = {}) {
  if (!soundKey) {
    return false;
  }

  const browserWindow = browserWindowFrom(options);
  if (!browserWindow) {
    return false;
  }

  if (soundKey === 'all-in' && speakAllIn(browserWindow)) {
    return true;
  }

  const audioContext = options.audioContext || getSharedAudioContext(browserWindow);
  if (!audioContext) {
    return false;
  }

  if (audioContext.state === 'suspended') {
    audioContext.resume?.().catch(() => {});
  }

  const startTime = audioContext.currentTime + 0.01;
  for (const tone of SOUND_PATTERNS[soundKey] || []) {
    scheduleTone(audioContext, tone, startTime + ((tone.offsetMs || 0) / 1000));
  }
  return true;
}

export function playActionSound(actionType, options = {}) {
  return playSoundKey(soundKeyForAction(actionType), options);
}

export function playEventSound(event, options = {}) {
  return playSoundKey(soundKeyForEvent(event), options);
}
